import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Demo from '../Demo';
import '../../styles/App.css';
import { 
  GlucocorticoidStatus, 
  HeightWeightChange, 
  PrecisionTestingAndScanMode, 
  NextStudyPrecisionCard, 
  FractureHistory, 
  PatientHistoryRetentionCard, 
  Benefit 
} from '../../components/features';
import NotesIcon from '@mui/icons-material/Notes';
import Button from '@mui/material/Button';
import { Grid, Typography, Box, Card, CardContent, Chip, Fade, Grow } from '@mui/material';
import { Assignment, Person, Dvr, FileCopy, Send, PlayArrow, ArrowForward, AutoAwesome, Verified, CalendarToday, Timer, PersonAdd } from '@mui/icons-material';
import { CenteredContainer } from '../../components/common';
import { FAQ } from '../FAQ';
import { Footer } from '../../components/layout';

function Home({isMobile, theme}) {
  const [visibleSections, setVisibleSections] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div style={{
        fontFamily: 'Open Sans, Arial, sans-serif',
        lineHeight: '1.6',
        margin: '0 auto',
        overflowX: 'hidden',
      }}>

        {/* Hero Section — Modern Design */}
        <section 
          id="hero"
          data-section="hero"
          style={{
            background: 'linear-gradient(135deg, rgba(10, 25, 41, 0.85) 0%, rgba(10, 61, 98, 0.85) 50%, rgba(13, 77, 122, 0.85) 100%), url("/hero.png")',
            padding: isMobile ? '80px 20px 60px' : '120px 20px 100px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? 'auto' : '85vh',
            display: 'flex',
            alignItems: 'center',
            backgroundSize: 'cover',
            backgroundPosition: 'right top',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Dark Overlay for Better Text Readability */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(10, 25, 41, 0.7) 0%, rgba(10, 61, 98, 0.6) 100%)',
            zIndex: 0,
          }} />
          {/* Animated Background Elements */}
          <Box sx={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(255, 107, 53, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 4s ease-in-out infinite',
            zIndex: 0,
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '50%': { transform: 'scale(1.1)', opacity: 0.8 },
            },
          }} />
          
          <Box sx={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(23, 162, 184, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 5s ease-in-out infinite',
            animationDelay: '1s',
            zIndex: 0,
          }} />

          {/* Top Accent Bar */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '5px',
            background: 'linear-gradient(90deg, #FF6B35 0%, #17A2B8 50%, #FF6B35 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s linear infinite',
            zIndex: 2,
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' },
            },
          }} />
          
          <CenteredContainer>
            <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={7}>
                <Fade in timeout={800}>
                  <Box>
                    {/* Badge */}
                    <Chip
                      icon={<AutoAwesome sx={{ color: '#FF6B35 !important' }} />}
                      label="DXA Workflow Automation"
                      sx={{
                        backgroundColor: 'rgba(255, 107, 53, 0.2)',
                        border: '1px solid rgba(255, 107, 53, 0.5)',
                        color: '#FF6B35',
                        fontWeight: '700',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        padding: '8px 4px',
                        marginBottom: '30px',
                        height: 'auto',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        '& .MuiChip-icon': {
                          color: '#FF6B35',
                        },
                      }}
                    />
                    
                    <Typography 
                      variant="h1" 
                      sx={{ 
                        color: '#FFFFFF', 
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: '800',
                        fontSize: isMobile ? '2.5rem' : '4rem',
                        marginBottom: '24px',
                        lineHeight: '1.1',
                        letterSpacing: '-1px',
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      Introducing{' '}
                      <Box component="span" sx={{ 
                        color: '#FF6B35',
                        textShadow: '0 2px 8px rgba(255, 107, 53, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
                      }}>
                        DXA Encode
                      </Box>
                    </Typography>
                    
                    <Box sx={{
                      width: '80px',
                      height: '4px',
                      background: 'linear-gradient(90deg, #FF6B35 0%, #17A2B8 100%)',
                      marginBottom: '30px',
                      borderRadius: '2px',
                      boxShadow: '0 2px 8px rgba(255, 107, 53, 0.4)',
                    }} />
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: '#FFFFFF', 
                        fontSize: isMobile ? '1.125rem' : '1.5rem',
                        marginBottom: '40px',
                        fontWeight: '400',
                        lineHeight: '1.7',
                        maxWidth: '600px',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      Developed by C-Encode, DXA Encode boosts clinic capacity by reducing technologist documentation time and enabling faster BMD reporting for radiologists.
                    </Typography>

                    {/* CTA Buttons */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: '16px',
                      marginTop: '40px',
                    }}>
                      <Button 
                        variant="contained"
                        size="large"
                        href="https://calendly.com/c-encode/demo"
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<CalendarToday />}
                        sx={{
                          background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                          color: '#FFFFFF',
                          padding: '16px 40px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)',
                          textTransform: 'none',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #E55A2B 0%, #D04A1B 100%)',
                            boxShadow: '0 12px 32px rgba(255, 107, 53, 0.6)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Book a Demo
                      </Button>
                      <Button 
                        variant="outlined"
                        size="large"
                        href="/reports"
                        endIcon={<ArrowForward />}
                        sx={{
                          borderColor: '#FFFFFF',
                          borderWidth: '2px',
                          color: '#FFFFFF',
                          padding: '16px 40px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          borderRadius: '12px',
                          textTransform: 'none',
                          backdropFilter: 'blur(10px)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            borderColor: '#FFFFFF',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        View Reports
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={5}>
                <Grow in timeout={1000}>
                  <Box sx={{
                    position: 'relative',
                    marginTop: isMobile ? '40px' : '0',
                    zIndex: 1,
                  }}>
                    {/* Stats Cards */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      marginTop: '40px',
                    }}>
                      <Card sx={{
                        background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.25) 0%, rgba(255, 107, 53, 0.15) 100%)',
                        backdropFilter: 'blur(15px)',
                        border: '1px solid rgba(255, 107, 53, 0.4)',
                        borderRadius: '16px',
                        padding: '20px 18px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
                        },
                      }}>
                        <Box>
                          <Timer sx={{ fontSize: '28px', color: '#FF6B35', marginBottom: '12px', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
                          <Typography sx={{ 
                            color: '#FFFFFF', 
                            fontSize: '1.25rem', 
                            fontWeight: '800', 
                            lineHeight: 1.3,
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            marginBottom: '8px',
                          }}>
                            40% less tech time per case
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          color: '#FFFFFF', 
                          fontSize: '0.875rem', 
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          opacity: 0.95,
                          fontWeight: '500',
                        }}>
                          25 min → 15 min per patient
                        </Typography>
                      </Card>
                      <Card sx={{
                        background: 'linear-gradient(135deg, rgba(23, 162, 184, 0.25) 0%, rgba(23, 162, 184, 0.15) 100%)',
                        backdropFilter: 'blur(15px)',
                        border: '1px solid rgba(23, 162, 184, 0.4)',
                        borderRadius: '16px',
                        padding: '20px 18px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
                        },
                      }}>
                        <Box>
                          <PersonAdd sx={{ fontSize: '28px', color: '#17A2B8', marginBottom: '12px', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
                          <Typography sx={{ 
                            color: '#FFFFFF', 
                            fontSize: '1.25rem', 
                            fontWeight: '800', 
                            lineHeight: 1.3,
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            marginBottom: '8px',
                          }}>
                            56% more cases scanned
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          color: '#FFFFFF', 
                          fontSize: '0.875rem', 
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          opacity: 0.95,
                          fontWeight: '500',
                        }}>
                          18 → 28 cases per day
                        </Typography>
                      </Card>
                      <Card sx={{
                        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.25) 0%, rgba(156, 39, 176, 0.15) 100%)',
                        backdropFilter: 'blur(15px)',
                        border: '1px solid rgba(156, 39, 176, 0.4)',
                        borderRadius: '16px',
                        padding: '20px 18px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
                        },
                      }}>
                        <Box>
                          <FileCopy sx={{ fontSize: '28px', color: '#9C27B0', marginBottom: '12px', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
                          <Typography sx={{ 
                            color: '#FFFFFF', 
                            fontSize: '1.25rem', 
                            fontWeight: '800', 
                            lineHeight: 1.3,
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            marginBottom: '8px',
                          }}>
                            90% faster reporting
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          color: '#FFFFFF', 
                          fontSize: '0.875rem', 
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          opacity: 0.95,
                          fontWeight: '500',
                        }}>
                          5 min → less than 30s
                        </Typography>
                      </Card>
                    </Box>
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </CenteredContainer>
        </section>

        {/* How It Works Section — Modern Card Grid */}
        <Box 
          id="how-it-works"
          data-section="how-it-works"
          sx={{
            background: 'linear-gradient(to bottom, #FFFFFF 0%, #F8F9FA 100%)',
            padding: isMobile ? '80px 20px' : '120px 20px',
            position: 'relative',
          }}
        >
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
              <Chip
                icon={<AutoAwesome />}
                label="Complete Workflow"
                sx={{
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                  color: '#FF6B35',
                  fontWeight: '700',
                  marginBottom: '24px',
                  padding: '8px 4px',
                }}
              />
              <Typography 
                variant="h2" 
                sx={{ 
                  color: '#0A3D62', 
                  fontFamily: 'Roboto, Arial, sans-serif',
                  marginBottom: '20px',
                  fontSize: isMobile ? '2rem' : '3rem',
                  fontWeight: '800',
                }}
              >
                How It Works
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  color: '#2E3A45',
                  maxWidth: '700px',
                  margin: '0 auto',
                  fontWeight: '400',
                }}
              >
                Five simple steps to transform your DXA reporting workflow
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {[
                {
                  icon: <Assignment sx={{ fontSize: '48px' }} />,
                  title: 'FRAX-ready patient history capture (multilingual)',
                  description: 'Patients complete structured fracture-risk history ahead of the exam, allowing technologists to review rather than conduct lengthy interviews—critical as questionnaire burden increases with FRAX.',
                  color: '#0A3D62',
                  gradient: 'linear-gradient(135deg, #0A3D62 0%, #0d4d7a 100%)',
                },
                {
                  icon: <Person sx={{ fontSize: '48px' }} />,
                  title: 'Auto-prepopulate from DXA exports and prior exams — no more manual lookup',
                  description: 'Key measurements and historical values are automatically brought forward from DXA exported results and prior exams, eliminating the need to search PACS, RIS, or prior reports.',
                  color: '#17A2B8',
                  gradient: 'linear-gradient(135deg, #17A2B8 0%, #4BAFD1 100%)',
                },
                {
                  icon: <Dvr sx={{ fontSize: '48px' }} />,
                  title: 'Automated follow-up calculations (no spreadsheets, no calculators)',
                  description: 'DXA Encode replaces manual calculations for height/weight changes, precision thresholds, and scan mode comparisons, ensuring consistent follow-up interpretation without ad-hoc math.',
                  color: '#FF6B35',
                  gradient: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                },
                {
                  icon: <FileCopy sx={{ fontSize: '48px' }} />,
                  title: 'Generate a CAR-aligned draft report (risk + follow-up)',
                  description: 'A structured draft report is generated with diagnostic category, fracture-risk assessment logic (CAROC/FRAX per site workflow), and follow-up recommendations—ready for radiologist review.',
                  color: '#0A3D62',
                  gradient: 'linear-gradient(135deg, #0A3D62 0%, #0d4d7a 100%)',
                },
                {
                  icon: <Send sx={{ fontSize: '48px' }} />,
                  title: 'Deliver outputs into the existing workflow (PACS + RIS/dictation)',
                  description: 'The completed worksheet is sent to PACS as a secondary capture, and the draft report is delivered into RIS/dictation workflows so radiologists sign off where they already work.',
                  color: '#17A2B8',
                  gradient: 'linear-gradient(135deg, #17A2B8 0%, #4BAFD1 100%)',
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Grow in={visibleSections['how-it-works']} timeout={600 + index * 200}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: '20px',
                      border: 'none',
                      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '5px',
                        background: item.gradient,
                      },
                    }}>
                      <CardContent sx={{ padding: '40px 32px' }}>
                        <Box sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '16px',
                          background: item.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '24px',
                          color: '#FFFFFF',
                        }}>
                          {item.icon}
                        </Box>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: '#0A3D62',
                            marginBottom: '16px',
                            fontWeight: '700',
                            fontSize: '1.25rem',
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#2E3A45',
                            lineHeight: '1.7',
                            fontSize: '1rem',
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </CenteredContainer>
        </Box>

        {/* Benefits Section — Modern Split Design */}
        <Box 
          id="benefits"
          data-section="benefits"
          sx={{
            background: 'linear-gradient(135deg, #0A1929 0%, #0A3D62 100%)',
            padding: isMobile ? '80px 20px' : '120px 20px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 107, 53, 0.1) 0%, transparent 50%)',
          }} />
          
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px', position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  color: '#FFFFFF', 
                  fontFamily: 'Roboto, Arial, sans-serif',
                  marginBottom: '20px',
                  fontSize: isMobile ? '2rem' : '3rem',
                  fontWeight: '800',
                }}
              >
                Benefits for Your Team
              </Typography>
              <Box sx={{
                width: '100px',
                height: '4px',
                background: 'linear-gradient(90deg, #FF6B35 0%, #17A2B8 100%)',
                margin: '0 auto',
                borderRadius: '2px',
              }} />
            </Box>
            
            <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={6}>
                <Fade in={visibleSections.benefits} timeout={800}>
                  <Card sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '40px 32px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    },
                  }}>
                    <Box sx={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '16px',
                      backgroundColor: 'rgba(255, 107, 53, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px',
                      color: '#FF6B35',
                      fontSize: '40px',
                    }}>
                      🏥
                    </Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#FFFFFF',
                        marginBottom: '24px',
                        fontWeight: '700',
                        fontSize: '1.75rem',
                      }}
                    >
                      For Clinics
                    </Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      '& li': {
                        marginBottom: '20px',
                        color: '#E0E0E0',
                        lineHeight: '1.7',
                        fontSize: '1rem',
                        paddingLeft: '24px',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '8px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#FF6B35',
                        },
                        '& strong': {
                          color: '#FFFFFF',
                          fontWeight: '700',
                        },
                      },
                    }}>
                      <li><strong>Patient-Entered Data in Their Own Language:</strong> Patients complete detailed history questionnaires in their language before the exam, which pre-populates the tech worksheet. Critical for FRAX transition—techs review answers instead of conducting lengthy interviews, saving significant time per patient.</li>
                      <li><strong>Minimized Data Entry: </strong> DXA Encode pulls data directly from the DXA machine, eliminating manual data entry and history searches.</li>
                      <li><strong>One-Time History Entry:</strong> Patient history (e.g., fractures, medications) is securely saved for easy access in future exams.</li>
                      <li><strong>Automated Calculations:</strong> Automatically tracks height, weight, precision values, and scan mode changes, reducing errors.</li>
                      <li><strong>Seamless PACS Integration:</strong> Worksheets go straight to PACS, eliminating scanning steps.</li>
                    </Box>
                  </Card>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in={visibleSections.benefits} timeout={1000}>
                  <Card sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '40px 32px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    },
                  }}>
                    <Box sx={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '16px',
                      backgroundColor: 'rgba(23, 162, 184, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px',
                      color: '#17A2B8',
                      fontSize: '40px',
                    }}>
                      📑
                    </Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#FFFFFF',
                        marginBottom: '24px',
                        fontWeight: '700',
                        fontSize: '1.75rem',
                      }}
                    >
                      For Radiologists
                    </Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      '& li': {
                        marginBottom: '20px',
                        color: '#E0E0E0',
                        lineHeight: '1.7',
                        fontSize: '1rem',
                        paddingLeft: '24px',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '8px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#17A2B8',
                        },
                        '& strong': {
                          color: '#FFFFFF',
                          fontWeight: '700',
                        },
                      },
                    }}>
                      <li><strong>Auto-Generated, CAR-Compliant Reports:</strong> DXA Encode generates reports with BMD values, change calculation, diagnostic category, CAROC/FRAX fracture risk, and follow-up recommendations.</li>
                      <li><strong>Consistent Data:</strong> Each report includes risk modifiers from the tech worksheet, custom LSCs, and area precision details, reducing the chance of inconsistencies.</li>
                      <li><strong>Streamlined Workflow:</strong> Reports are accessible in dictation software / RIS, allowing quick review and sign-off in the existing workflow.</li>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          </CenteredContainer>
        </Box>

        {/* Demo CTA Section */}
        <Box 
          id="demo-cta"
          data-section="demo-cta"
          sx={{
            background: 'linear-gradient(135deg, #1D1D1D 0%, #2E3A45 100%)',
            padding: isMobile ? '60px 20px' : '80px 20px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255, 107, 53, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          
          <CenteredContainer>
            <Fade in={visibleSections['demo-cta']} timeout={800}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '24px',
                padding: isMobile ? '40px 30px' : '50px 60px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '30px',
                position: 'relative',
                zIndex: 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                  <Box sx={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <NotesIcon sx={{ color: '#FFFFFF', fontSize: '40px' }} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#FFFFFF', 
                        margin: 0, 
                        fontSize: isMobile ? '1.5rem' : '1.75rem',
                        fontWeight: '700',
                        marginBottom: '8px',
                      }}
                    >
                      Want an interactive way to explore our tool?
                    </Typography>
                    <Typography 
                      sx={{ 
                        color: '#E0E0E0', 
                        margin: 0, 
                        fontSize: '1rem',
                      }}
                    >
                      Experience DXA Encode with our interactive demo!
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: '16px',
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto',
                }}>
                  <Button 
                    variant="contained"
                    size="large"
                    href="/demo"
                    startIcon={<PlayArrow />}
                    sx={{
                      background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                      color: '#FFFFFF',
                      padding: '14px 32px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)',
                      textTransform: 'none',
                      minWidth: isMobile ? '100%' : '180px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #E55A2B 0%, #D04A1B 100%)',
                        boxShadow: '0 12px 32px rgba(255, 107, 53, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Try Now
                  </Button>
                  <Button 
                    variant="outlined"
                    size="large"
                    href="/reports"
                    endIcon={<ArrowForward />}
                    sx={{
                      borderColor: '#FFFFFF',
                      borderWidth: '2px',
                      color: '#FFFFFF',
                      padding: '14px 32px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      borderRadius: '12px',
                      textTransform: 'none',
                      backdropFilter: 'blur(10px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      minWidth: isMobile ? '100%' : '220px',
                      '&:hover': {
                        borderColor: '#FFFFFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    View Reports
                  </Button>
                </Box>
              </Card>
            </Fade>
          </CenteredContainer>
        </Box>

        {/* Interactive Components Section */}
        <Box 
          id="features"
          data-section="features"
          sx={{
            background: 'linear-gradient(to bottom, #FFFFFF 0%, #F8F9FA 100%)',
            padding: isMobile ? '80px 20px' : '120px 20px',
            position: 'relative',
          }}
        >
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
              <Chip
                icon={<AutoAwesome />}
                label="Key Features"
                sx={{
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                  color: '#FF6B35',
                  fontWeight: '700',
                  marginBottom: '24px',
                  padding: '8px 4px',
                }}
              />
              <Typography 
                variant="h2" 
                sx={{ 
                  color: '#0A3D62', 
                  fontFamily: 'Roboto, Arial, sans-serif',
                  marginBottom: '20px',
                  fontSize: isMobile ? '2rem' : '3rem',
                  fontWeight: '800',
                }}
              >
                Powerful Features
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  color: '#2E3A45',
                  maxWidth: '700px',
                  margin: '0 auto',
                  fontWeight: '400',
                }}
              >
                Explore the interactive components that make DXA Encode powerful and efficient
              </Typography>
            </Box>
            <Box>
            <Benefit
              title="Patient-Entered History in Their Own Language"
              description="Patients complete their own detailed history—including fracture details, medications, and FRAX risk factors—in their own language before their exam. This information automatically pre-populates the technologist's worksheet, transforming the workflow from time-consuming interviews to quick reviews. This is especially critical with the transition to FRAX, which requires extensive patient questionnaires. Technologists save significant time by simply verifying pre-populated answers rather than asking dozens of questions. The system securely retains this information for future exams, making subsequent visits even faster."
              component={PatientHistoryRetentionCard}
            />

            <Benefit
              title="Automated Height & Weight Change Detection"
              description="The Height and Weight Tracking component of DXA Encode automatically compares the patient’s current measurements with their previous records, flagging significant changes—such as weight changes over 10% or height reductions exceeding 2 cm. Previously, technologists would need to calculate these differences manually using Excel or a calculator, which was time-consuming and prone to errors. With DXA Encode, this entire process is automated and pre-populated, saving valuable time and ensuring accurate, instant identification of physical changes relevant for fracture risk analysis."
              component={HeightWeightChange}
            />

            <Benefit
              title="Glucocorticoid Use Assessment Simplification"
              description="DXA Encode enables technologists to quickly and accurately assess if the patient has met the CAR/FRAX critical threshold for glucocorticoid use—specifically, whether they have taken an equivalent of 7.5 mg of prednisone for at least 90 days within the past 365 days. The tool simplifies this process by automatically converting different glucocorticoid medications into prednisone equivalents, ensuring accurate and standardized assessment. This automation not only speeds up the evaluation but also supports compliance with CAR and FRAX guidelines, providing radiologists with a reliable assessment of glucocorticoid impact on bone health."
              component={GlucocorticoidStatus}
            />

            <Benefit
              title="Streamlined Fracture History Documentation"
              description="DXA Encode provides an interactive Fracture History component where users can enter details of any fractures the patient has had, including location, cause (e.g., fragility), and age at the time of the fracture. Once this information is entered, the system retains it for future visits, so technologists don’t need to re-enter it, streamlining the workflow significantly."
              component={FractureHistory}
            />

            <Benefit
              title="Pre-Set Precision Ranges for Accurate Scans"
              description="DXA Encode offers pre-set precision ranges for each scan area, guiding technologists on target measurement thresholds before scanning begins. By automating the 2% variance check, DXA Encode saves time, reduces manual effort, and minimizes errors, making it easier for technologists to meet precise scanning requirements every time."
              component={NextStudyPrecisionCard}
            />

            <Benefit
              title="Automated Precision Testing and Scan Mode Tracking"
              description="DXA Encode automatically calculates precision for each scanned body region (AP Spine, Left/Right Femur, Left/Right Forearm, and Total Body) using its Area, flagging any deviations over a 2% threshold. It also tracks changes in scan mode (e.g., Standard, Thin) from previous exams, providing essential context for radiologists. Previously, these precision checks were done manually with calculators or spreadsheets, which was time-consuming and prone to errors. DXA Encode automates the process, saving time, reducing errors, and ensuring consistent bone density tracking."
              component={PrecisionTestingAndScanMode}
            />

            <Benefit
              title="Seamless PACS Integration for Worksheets"
              description="Once all data is reviewed, the completed worksheet is automatically sent to the PACS as a secondary capture image, where it is available for radiologists to access and review. This seamless integration with PACS eliminates the need for manual data entry, ensuring that radiologists receive a fully compliant report with consistent and accurate information."
              component={() => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src="/worksheet.jpg" 
                    alt="Worksheet" 
                    style={{ 
                      maxWidth: '100%', 
                      height: 'auto', 
                      marginLeft: isMobile ? '0' : '20px' 
                    }} 
                  />
                </div>
              )}
            />

            <Benefit
              title="Ready-to-Review BMD Reports In RIS / Dictation Software"
              description="Once the technologist submits the completed worksheet, DXA Encode automatically generates a fully compliant BMD report based on CAR (Canadian Association of Radiologists) standards with CAROC/FRAX fracture risk assessments. This report includes diagnostic categories, fracture risk assessments, and follow-up recommendations, making it ready for the radiologist to review and sign off with minimal additional input. This automation not only saves time but also ensures consistency and regulatory compliance across all reports."
              component={() => (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                  <div style={{ flex: '1', marginRight: '10px' }}>
                    <Demo basicMode={true} initStudyType={'followup'}/>
                  </div>
                </div>
              )}
            />
            </Box>
          </CenteredContainer>
        </Box>

        {/* Final CTA Section */}
        <Box 
          id="contact"
          data-section="contact"
          sx={{
            background: 'linear-gradient(135deg, #0A1929 0%, #0A3D62 100%)',
            padding: isMobile ? '80px 20px' : '120px 20px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255, 107, 53, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          
          <CenteredContainer>
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Box sx={{
                backgroundColor: 'rgba(255, 107, 53, 0.2)',
                borderRadius: '50%',
                width: '100px',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px',
              }}>
                <Verified sx={{ fontSize: '50px', color: '#FF6B35' }} />
              </Box>
              
              <Typography 
                variant="h2" 
                sx={{ 
                  color: '#FFFFFF',
                  fontFamily: 'Roboto, Arial, sans-serif',
                  fontSize: isMobile ? '2rem' : '3rem',
                  fontWeight: '800',
                  marginBottom: '20px',
                }}
              >
                Ready to Increase Your Clinic's DXA Capacity?
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#E0E0E0',
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  marginBottom: '50px',
                  maxWidth: '600px',
                  margin: '0 auto 50px',
                  fontWeight: '400',
                }}
              >
                Book a demo to see how DXA Encode can transform your workflow
              </Typography>
              
              <Button 
                variant="contained"
                size="large"
                href="https://calendly.com/c-encode/demo"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<CalendarToday />}
                sx={{
                  background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                  color: '#FFFFFF',
                  padding: '20px 40px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E55A2B 0%, #D04A1B 100%)',
                    boxShadow: '0 12px 32px rgba(255, 107, 53, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Book a Demo
              </Button>
            </Box>
          </CenteredContainer>
        </Box>
        <FAQ />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default Home;